import { useEffect, useState } from "react";
import type { Rpc } from "../lib/rpc";

interface Partner {
  name: string;
  nickname: string;
  x?: string;
  instagram?: string;
  onlyfans?: string;
  fansly?: string;
  manyvids?: string;
  fetlife?: string;
  added_at?: string;
}

const FIELDS: Array<{ key: keyof Partner; label: string; placeholder: string }> = [
  { key: "x",        label: "X (Twitter)", placeholder: "https://x.com/username" },
  { key: "instagram", label: "Instagram",  placeholder: "https://instagram.com/username" },
  { key: "fetlife",  label: "FetLife",     placeholder: "https://fetlife.com/username" },
  { key: "onlyfans", label: "OnlyFans",    placeholder: "https://onlyfans.com/username" },
  { key: "fansly",   label: "Fansly",      placeholder: "https://fansly.com/username" },
  { key: "manyvids", label: "ManyVids",    placeholder: "https://manyvids.com/…" },
];

const EMPTY_PARTNER: Partner = {
  name: "", nickname: "",
  x: "", instagram: "", onlyfans: "", fansly: "", manyvids: "", fetlife: "",
};

export function PartnersPage({ rpc, electronConnected }: { rpc: Rpc; electronConnected: boolean }) {
  const [partners, setPartners] = useState<Partner[]>([]);
  const [editing, setEditing] = useState<{ index: number; data: Partner } | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  useEffect(() => {
    if (!electronConnected) return;
    refresh();
  }, [electronConnected, rpc]);

  async function refresh() {
    try {
      const data = await rpc.call<Partner[]>("get-partners");
      setPartners(Array.isArray(data) ? data : []);
    } catch (e) { setErr((e as Error).message); }
  }

  async function commit(next: Partner[], reason: string) {
    setBusy(true);
    setErr(null);
    try {
      const r = await rpc.call<{ ok: boolean; error?: string }>("save-partners", { partners: next, reason });
      if (!r.ok) throw new Error(r.error || "save failed");
      setPartners(next);
      setEditing(null);
      setSavedAt(Date.now());
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  function startEdit(idx: number) {
    setEditing({ index: idx, data: { ...partners[idx] } });
  }
  function startAdd() {
    setEditing({ index: -1, data: { ...EMPTY_PARTNER } });
  }
  function cancelEdit() {
    setEditing(null);
    setErr(null);
  }

  async function saveEdit() {
    if (!editing) return;
    if (!editing.data.name.trim() || !editing.data.nickname.trim()) {
      setErr("Name and Nickname are required.");
      return;
    }
    const next = partners.slice();
    if (editing.index === -1) {
      next.push({ ...editing.data, added_at: new Date().toISOString() });
      await commit(next, `Added ${editing.data.nickname}`);
    } else {
      next[editing.index] = { ...editing.data };
      await commit(next, `Edited ${editing.data.nickname}`);
    }
  }

  async function deletePartner(idx: number) {
    const target = partners[idx];
    if (!confirm(`Delete ${target.name} (${target.nickname})?`)) return;
    const next = partners.filter((_, i) => i !== idx);
    await commit(next, `Deleted ${target.nickname}`);
  }

  if (!electronConnected) {
    return (
      <section className="settings-page">
        <header className="settings-page-header"><h2>👥 Collab Partners</h2></header>
        <div className="empty">Electron must be running on the worker PC.</div>
      </section>
    );
  }

  return (
    <section className="settings-page">
      <header className="settings-page-header">
        <h2>👥 Collab Partners</h2>
        <div className="header-actions">
          {savedAt && Date.now() - savedAt < 4000 && <span className="ok">✓ saved</span>}
          {err && <span className="err">{err}</span>}
          <button className="primary" onClick={startAdd} disabled={busy}>+ Add</button>
        </div>
      </header>

      <div className="partners-list">
        <div className="partners-row partners-row-header">
          <span>Name</span>
          <span>Nickname</span>
          <span>Platforms</span>
          <span></span>
        </div>
        {partners.map((p, idx) => {
          const platforms = FIELDS.filter((f) => p[f.key]).map((f) => f.label.split(" ")[0]);
          return (
            <div key={`${p.name}-${idx}`} className="partners-row">
              <span>{p.name}</span>
              <span className="muted">{p.nickname}</span>
              <span className="muted">{platforms.join(", ") || "—"}</span>
              <span className="row-actions">
                <button onClick={() => startEdit(idx)} disabled={busy}>Edit</button>
                <button onClick={() => deletePartner(idx)} disabled={busy} className="danger">Delete</button>
              </span>
            </div>
          );
        })}
        {partners.length === 0 && <div className="empty">No partners yet. Click + Add.</div>}
      </div>

      {editing && (
        <div className="modal-backdrop" onClick={cancelEdit}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <header className="modal-header">
              <h3>{editing.index === -1 ? "Add Partner" : `Edit Partner: ${editing.data.name}`}</h3>
              <button className="modal-close" onClick={cancelEdit}>✕</button>
            </header>
            <div className="modal-body">
              <div className="form-row two-col">
                <div>
                  <label>Name *</label>
                  <input
                    type="text" required
                    value={editing.data.name}
                    onChange={(e) => setEditing({ ...editing, data: { ...editing.data, name: e.target.value } })}
                  />
                </div>
                <div>
                  <label>Nickname *</label>
                  <input
                    type="text" required
                    value={editing.data.nickname}
                    onChange={(e) => setEditing({ ...editing, data: { ...editing.data, nickname: e.target.value } })}
                  />
                </div>
              </div>
              <div className="form-fields-grid">
                {FIELDS.map((f) => (
                  <div key={f.key as string}>
                    <label>{f.label}</label>
                    <input
                      type="url"
                      placeholder={f.placeholder}
                      value={(editing.data[f.key] as string) || ""}
                      onChange={(e) => setEditing({ ...editing, data: { ...editing.data, [f.key]: e.target.value } })}
                    />
                  </div>
                ))}
              </div>
              {err && <p className="err">{err}</p>}
            </div>
            <footer className="modal-footer">
              <button onClick={cancelEdit} disabled={busy}>Cancel</button>
              <button className="primary" onClick={saveEdit} disabled={busy}>
                {busy ? "Saving…" : "💾 Save"}
              </button>
            </footer>
          </div>
        </div>
      )}
    </section>
  );
}
